import { auth } from '../firebase/config'; // db might still be needed for addSiteMedia
import { addSiteMedia } from '../firebase/siteMedia';
import {
  uploadFileToR2,
  getR2PublicUrl
} from '../services/r2Service';

// Supported file types and size limits
const SUPPORTED_FILE_TYPES = {
  // Images
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  // Videos
  'video/mp4': 'video',
  'video/webm': 'video',
  // 3D Models - Web optimized
  'model/gltf-binary': '3d',
  'model/gltf+json': '3d',
  // 3D Models - Common formats
  'model/obj': '3d',
  'model/stl': '3d',
  'model/ply': '3d',
  'model/ifc': '3d',
  'application/ifc': '3d',
  // Point clouds
  'application/las': '3d',
  'application/laz': '3d',
  // Generic binary for 3D formats that may not have proper MIME types
  'application/octet-stream': '3d'
};

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

// R2 path will be the objectKey itself.
// The URL will be constructed using getR2PublicUrl(objectKey).
function createFileData(objectKey, fileType, file, metadata = {}) {
  return {
    url: getR2PublicUrl(objectKey), // Dynamically generated
    path: objectKey, // This is the R2 object key
    type: fileType || '',
    name: file?.name || '',
    size: Number(file?.size) || 0,
    uploadedBy: auth.currentUser?.uid || 'anonymous',
    features: metadata.features || {},
    predictions: Array.isArray(metadata.predictions) ? metadata.predictions : [],
    sublocationId: metadata.sublocationId || null,
    coordinates: metadata.coordinates || null,
    credits: metadata.credits || null,
    creditProfileId: metadata.creditProfileId || null,
    uploadedAt: new Date().toISOString()
  };
}

// Helper function to detect 3D file type by extension
const detect3DTypeByExtension = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  const extensions3D = ['ply', 'obj', 'stl', 'gltf', 'glb', 'ifc', 'las', 'laz'];
  return extensions3D.includes(ext) ? '3d' : null;
};

const validateFileType = (file) => {
  // First check if we have a predetermined type from the upload component
  if (file.fileType && ['image', 'video', '3d'].includes(file.fileType)) {
    return file.fileType;
  }
  
  // Check the MIME type first
  let type = SUPPORTED_FILE_TYPES[file.type];
  
  // If MIME type is generic or unknown, try to detect by file extension
  if (!type || (file.type === 'application/octet-stream' && file.name)) {
    const detected3D = detect3DTypeByExtension(file.name);
    if (detected3D) {
      type = detected3D;
    }
  }
  
  if (!type) {
    throw new Error(`Unsupported file type: ${file.type} (${file.name || 'unknown filename'})`);
  }
  
  return type;
};

export async function uploadFiles(files, siteId) {
  const uploadedFilesData = [];
  const errors = [];

  for (const file of files) {
    try {
      const fileType = validateFileType(file);

      if (file.size > MAX_FILE_SIZE) {
        errors.push(`File ${file.name} exceeds limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
        continue;
      }

      // Upload the file directly to R2 via worker
      const uploadResult = await uploadFileToR2(file);
      const objectKey = uploadResult.objectKey;

      const fileData = createFileData(
        objectKey,
        fileType,
        file,
        {
          features: file.features,
          predictions: file.predictions
        }
      );

      uploadedFilesData.push(fileData);

    } catch (error) {
      console.error('Error uploading file to R2:', error);
      errors.push(`Failed to upload ${file.name}: ${error.message}`);
    }
  }

  if (siteId && uploadedFilesData.length > 0) {
    try {
      await addSiteMedia(siteId, { items: uploadedFilesData });
    } catch (error) {
      console.error('Error updating site media after R2 upload:', error);
      errors.push('Failed to update site media database after R2 upload.');
    }
  }

  return {
    success: uploadedFilesData.length > 0 && errors.length === 0,
    files: uploadedFilesData,
    errors: errors.length > 0 ? errors : null,
  };
}

export function uploadWithProgress(file, siteId, metadata = {}, onProgress) {
  return new Promise(async (resolve, reject) => {
    try {
      const fileType = validateFileType(file);

      if (file.size > MAX_FILE_SIZE) {
        reject(new Error(`File size exceeds limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`));
        return;
      }

      // Upload the file directly to R2 via worker
      const uploadResult = await uploadFileToR2(file, (progress) => {
        if (onProgress) onProgress(progress);
      });
      const objectKey = uploadResult.objectKey;
      
      const fileData = createFileData(
        objectKey,
        fileType,
        file,
        metadata
      );

      if (siteId) {
        try {
          await addSiteMedia(siteId, { items: [fileData] });
        } catch (error) {
          console.error('Error updating site media metadata after R2 upload:', error);
          resolve({
            ...fileData,
            warning: 'File uploaded successfully to R2 but metadata update failed. The file may need to be re-uploaded or metadata manually fixed.'
          });
          return;
        }
      }
      resolve(fileData);
    } catch (error) {
      console.error('Error in uploadWithProgress to R2:', error);
      reject(new Error(`Upload setup or execution failed: ${error.message}`));
    }
  });
}
